import { Box, Center } from '@chakra-ui/react';
import {
    BorderRadius,
    BorderWidth,
    Colors,
    FontSizes,
    Opacity,
    Spacing,
} from 'app/koddi-components/src/_ChakraComponents/ChakraTheme';
import { CRow } from 'app/koddi-components/src/_ChakraComponents/Layouts';
import { FC, useCallback } from 'react';
import { components as ReactSelectComponents, OptionProps } from 'react-select';
import { CCheckMarkIcon } from 'app/koddi-components/src/_ChakraComponents/Icons';
import { CTextButton } from 'app/koddi-components/src/_ChakraComponents/Buttons';
import { CIconTooltip } from 'app/koddi-components/src/_ChakraComponents/Tooltip';
import { OptionWithProps } from '../SelectField.types';
import { TooltipWrapper } from './TooltipWrapper.component';

const OptionContent: FC<{
    isSelected: boolean;
    isDisabled?: boolean;
    isFocused?: boolean;
    tooltip?: string;
}> = ({ isSelected, isDisabled, isFocused, children, tooltip }) => {
    return (
        <CRow
            noBreak
            alignItems="center"
            gap={Spacing.XS}
            p={Spacing.XS}
            opacity={isDisabled ? Opacity.Disabled : undefined}
            cursor={isDisabled ? 'not-allowed' : undefined}
            bgColor={isFocused ? Colors.grayLighter : undefined}
            _hover={
                isDisabled ? { backgroundColor: Colors.grayLighter } : undefined
            }
        >
            <Center
                height="16px"
                minW="16px"
                maxW="16px"
                backgroundColor={isSelected ? Colors.primary : 'transparent'}
                borderColor={isSelected ? Colors.primary : Colors.grayLight}
                borderWidth={BorderWidth.Thin}
                borderStyle="solid"
                borderRadius={BorderRadius.XS}
            >
                {isSelected && (
                    <CCheckMarkIcon fill={Colors.white} width="70%" />
                )}
            </Center>
            <Box
                overflow="hidden"
                textOverflow="ellipsis"
                flexGrow={1}
                paddingRight={isFocused ? Spacing.LR : undefined}
            >
                {children}
            </Box>
            {tooltip && (
                <CIconTooltip
                    label={tooltip}
                    placement="bottom-start"
                    isDisabled={!tooltip}
                />
            )}
        </CRow>
    );
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const OptionWithCheckbox = ({
    isSelected,
    children,
    innerProps,
    ...rest
}: OptionProps<OptionWithProps, boolean>): JSX.Element | null => {
    const { isFocused, setValue, data, options } = rest;

    const style = {
        backgroundColor: 'transparent',
        color: 'inherit',
        padding: 0,
    };
    const handleOnlyClick = useCallback(() => {
        const permanentOptions = options.filter(
            (option) => !!option?.isPermanent
        );
        setValue([...permanentOptions, data], 'set-value');
    }, [options, setValue, data]);

    if (data?.isPermanent) {
        return null;
    }
    if (data?.isDisabled) {
        return (
            <OptionContent
                isSelected={isSelected}
                isDisabled
                tooltip={data?.tooltip}
            >
                {children}
            </OptionContent>
        );
    }

    return (
        <Box position="relative">
            <TooltipWrapper
                label={children}
                openDelay={500}
                isDisabled={!rest.selectProps?.hasTooltip}
                placement="bottom-start"
            >
                <ReactSelectComponents.Option
                    isSelected={isSelected}
                    {...rest}
                    /* @ts-ignore */
                    innerProps={{ ...innerProps, style }}
                >
                    <OptionContent
                        isFocused={isFocused}
                        isSelected={isSelected}
                    >
                        {children}
                    </OptionContent>
                </ReactSelectComponents.Option>
            </TooltipWrapper>
            {isFocused && (
                <CTextButton
                    position="absolute"
                    top={0}
                    // translateY="-25%"
                    right={0}
                    onClick={handleOnlyClick}
                    testId="only-selection"
                    fontSize={FontSizes.sm}
                    background={Colors.grayLighter}
                    p={Spacing.XS}
                    px={Spacing.XS}
                    minWidth={Spacing.LR}
                    height="100%"
                    justifyContent="center"
                    alignContent="center"
                    display="flex"
                >
                    only
                </CTextButton>
            )}
        </Box>
    );
};
